// MapRouteToggleButtonsGoogle.ts
// Handles rendering and logic for route toggle buttons for Google and MileMaker routes

import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";

// Add global type declaration for our temporary route data storage
declare global {
    interface Window {
        __routeDataForToggle?: any[];
        __lastRouteToggleOptions?: any;
        __mapToggleInstances?: Record<string, MapRouteToggleButtonsGoogle>;
    }
}

const log = DesignableObjectLogManager.getLogger("MapRouteToggleButtonsGoogle");

export interface RouteToggleOptions {
    showGoogle: boolean;
    showTrimble: boolean;  // Trimble includes both PC*Miler On Prem and PC*Miler Web
    showMileMaker: boolean;
    routeData: any[];
    showProposedRoute?: boolean;
    _userToggled?: boolean; // Flag to indicate when the user has toggled the visibility
}

export type RouteVisibility = {
    google: boolean;
    trimble: boolean; // Trimble includes both PC*Miler On Prem and PC*Miler Web
    milemaker: boolean;
};

export class MapRouteToggleButtonsGoogle {
    private containerId: string;
    private container: HTMLElement | null = null;
    private routeVisibility: RouteVisibility;
    private mapDiv: HTMLElement;
    private mapInstanceId: string;
    private originalMapId: string; // Store the original map ID to track across fullscreen
    private uniqueInstanceId: string; // Truly unique ID for this instance
    private callbacks: {
        google: (visible: boolean) => void;
        milemaker: (visible: boolean) => void;
        trimble?: (visible: boolean) => void;
        handleRouteToggle?: (routeData: any[]) => void;
        handleTrimbleRouteToggle?: (routeData: any[]) => void;
    };

    constructor(mapDiv: HTMLElement, routeVisibility: RouteVisibility, callbacks: {
        google: (visible: boolean) => void;
        milemaker: (visible: boolean) => void;
        trimble?: (visible: boolean) => void;
        handleRouteToggle?: (routeData: any[]) => void;
        handleTrimbleRouteToggle?: (routeData: any[]) => void;
    }) {
        this.mapDiv = mapDiv;
        this.routeVisibility = routeVisibility;
        this.callbacks = callbacks;

        // Store the original map ID - this identifies which map this is across fullscreen transitions
        this.originalMapId = mapDiv.id || '';
        this.mapInstanceId = this.originalMapId;

        // Create a truly unique ID for this toggle instance that will never collide
        const timestamp = new Date().getTime();
        const random = Math.floor(Math.random() * 1000000);
        this.uniqueInstanceId = `${this.originalMapId}-${timestamp}-${random}`;

        // Use this unique ID for the container
        this.containerId = `route-toggle-container-${this.uniqueInstanceId}`;

        // Register this instance in a global registry to help with fullscreen handling
        if (!(window as any).__mapToggleInstances) {
            (window as any).__mapToggleInstances = {};
        }
        (window as any).__mapToggleInstances[this.uniqueInstanceId] = this;

        // First check if this map div already has a toggle assigned to it
        const existingInstanceId = mapDiv.dataset.toggleInstanceId;
        if (existingInstanceId) {
            log.debug(`MapRouteToggleButtonsGoogle: Map already has toggle ID ${existingInstanceId}, adding new ID ${this.uniqueInstanceId}`);
        }

        // Add a data attribute to track the original map ID (for fullscreen transitions)
        mapDiv.dataset.originalMapId = this.originalMapId;

        // Store the unique instance ID as a data attribute directly on the map div
        mapDiv.dataset.toggleInstanceId = this.uniqueInstanceId;

        log.debug(`MapRouteToggleButtonsGoogle: Created for map with ID ${this.mapInstanceId}`);
    }

    public render(options: RouteToggleOptions) {
        log.debug('MapRouteToggleButtonsGoogle: Rendering route toggle buttons with options:', {
            showGoogle: options.showGoogle,
            showTrimble: options.showTrimble,
            showMileMaker: options.showMileMaker,
            routeDataLength: options.routeData?.length || 0,
            showProposedRoute: options.showProposedRoute,
            _userToggled: options._userToggled,
            currentVisibility: {
                google: this.routeVisibility.google,
                milemaker: this.routeVisibility.milemaker,
                trimble: this.routeVisibility.trimble
            }
        });

        // Store route data and options globally so they can be accessed in fullscreen mode
        if (options.routeData && options.routeData.length > 0) {
            (window as any).__routeDataForToggle = options.routeData;
            log.debug('MapRouteToggleButtonsGoogle: Route data stored globally with length:', options.routeData.length);
        }

        // Store the current options for use in fullscreen mode
        (window as any).__lastRouteToggleOptions = options;
        log.debug('MapRouteToggleButtonsGoogle: Route options stored globally');

        // Handle MileMaker visibility
        // IMPORTANT: When showProposedRoute is true and _userToggled is not set,
        // we want the initial state to be visible (true)
        if (options.showMileMaker) {
            if (options._userToggled) {
                // Respect current visibility state when user has toggled
                log.debug('User toggled MileMaker, keeping current visibility:', this.routeVisibility.milemaker);
            }
            else if (options.showProposedRoute !== undefined) {
                // Respect showProposedRoute when available
                this.routeVisibility.milemaker = !!options.showProposedRoute;
                log.debug('Setting MileMaker visibility based on showProposedRoute:',
                    this.routeVisibility.milemaker,
                    'showProposedRoute=', options.showProposedRoute);
            }
            else if (!('milemaker' in this.routeVisibility) || this.routeVisibility.milemaker === undefined) {
                // Initial default when nothing is specified
                this.routeVisibility.milemaker = false;
                log.debug('Initializing MileMaker visibility to default (false)');
            }
        }

        // Handle Trimble visibility (similar to MileMaker)
        if (options.showTrimble) {
            if (options._userToggled) {
                // Respect current visibility state when user has toggled
                log.debug('User toggled Trimble, keeping current visibility:', this.routeVisibility.trimble);
            }
            else if (options.showProposedRoute !== undefined) {
                // Respect showProposedRoute when available (same as MileMaker)
                this.routeVisibility.trimble = !!options.showProposedRoute;
                log.debug('Setting Trimble visibility based on showProposedRoute:',
                    this.routeVisibility.trimble,
                    'showProposedRoute=', options.showProposedRoute);
            }
            else if (!('trimble' in this.routeVisibility) || this.routeVisibility.trimble === undefined) {
                // Initial default when nothing is specified
                this.routeVisibility.trimble = false;
                log.debug('Initializing Trimble visibility to default (false)');
            }
        }

        // For Google: initialize to false if not set
        if (options.showGoogle && (!('google' in this.routeVisibility) || this.routeVisibility.google === undefined)) {
            this.routeVisibility.google = false;
            log.debug('MapRouteToggleButtonsGoogle: Initializing Google route visibility to false');
        } else if (options.showGoogle) {
            log.debug('MapRouteToggleButtonsGoogle: Using existing Google route visibility:', this.routeVisibility.google);
        } else {
            log.debug('MapRouteToggleButtonsGoogle: Google route toggle not shown (options.showGoogle is false)');
        }

        // Define button configurations
        const buttonConfigs = [
            options.showGoogle && {
                color: '#0000ff',
                visible: this.routeVisibility.google,
                baseLabel: 'Google non-truck route',
                onClick: () => {
                    log.debug('MapRouteToggleButtonsGoogle: Google route button clicked, toggling from',
                        this.routeVisibility.google, 'to', !this.routeVisibility.google);
                    this.routeVisibility.google = !this.routeVisibility.google;
                    this.callbacks.google(this.routeVisibility.google);
                    // Pass updated visibility state to render
                    this.render({ ...options });
                }
            },
            options.showMileMaker && {
                color: '#ff0000',
                visible: this.routeVisibility.milemaker,
                baseLabel: 'MileMaker Truck Route',
                onClick: () => {
                    // Toggle the visibility state
                    const newVisibility = !this.routeVisibility.milemaker;
                    this.routeVisibility.milemaker = newVisibility;
                    log.debug('Toggled MileMaker visibility to:', newVisibility);

                    // Call the callback to update the map
                    this.callbacks.milemaker(newVisibility);

                    // Call handleRouteToggle to fetch data if needed
                    if (newVisibility) {
                        this.callbacks.handleRouteToggle?.(options.routeData);
                    }

                    // Re-render with updated visibility state
                    this.render({
                        ...options,
                        _userToggled: true
                    });
                }
            },
            options.showTrimble && {
                color: '#FF0000',
                visible: this.routeVisibility.trimble,
                baseLabel: 'PC*Miler Truck Route',
                onClick: () => {
                    // Toggle the visibility state
                    const newVisibility = !this.routeVisibility.trimble;
                    this.routeVisibility.trimble = newVisibility;
                    log.debug('Toggled Trimble visibility to:', newVisibility);

                    // Call the callback to update the map
                    if (this.callbacks.trimble) {
                        this.callbacks.trimble(newVisibility);
                    }

                    // Call handleRouteToggle to fetch data if needed - match the MileMaker pattern
                    if (newVisibility && this.callbacks.handleTrimbleRouteToggle) {
                        this.callbacks.handleTrimbleRouteToggle(options.routeData);
                    }

                    // Re-render with updated visibility state
                    this.render({
                        ...options,
                        _userToggled: true
                    });
                }
            }
        ].filter(Boolean);

        // If no buttons should be shown, remove any existing container and exit
        if (buttonConfigs.length === 0) {
            log.debug('MapRouteToggleButtonsGoogle: No toggle buttons to show, removing container if it exists');

            // Look for containers only within this map instance
            const existingContainer = this.mapDiv.querySelector(`#${this.containerId}`);
            if (existingContainer && existingContainer.parentNode) {
                existingContainer.parentNode.removeChild(existingContainer);
            }

            // Also check for any orphaned containers with same ID elsewhere in the document
            // This can happen during fullscreen transitions
            const orphanedContainers = document.querySelectorAll(`#${this.containerId}`);
            orphanedContainers.forEach(orphan => {
                if (orphan.parentNode && orphan !== existingContainer) {
                    log.debug('MapRouteToggleButtonsGoogle: Removing orphaned container');
                    orphan.parentNode.removeChild(orphan);
                }
            });

            this.container = null;
            return;
        }

        // Log button configuration state
        log.debug('MapRouteToggleButtonsGoogle: Creating button configurations with:', {
            showGoogle: options.showGoogle,
            googleVisible: this.routeVisibility.google,
            showMileMaker: options.showMileMaker,
            mileMakerVisible: this.routeVisibility.milemaker,
            buttonCount: buttonConfigs.length
        });

        // Only proceed with creating the container if there are buttons to show
        // First look exclusively within this specific map div
        let container = this.mapDiv.querySelector(`#${this.containerId}`) as HTMLElement;

        log.debug('MapRouteToggleButtonsGoogle: Looking for container:', {
            containerId: this.containerId,
            containerFound: !!container,
            mapDivId: this.mapDiv?.id || 'no-id',
            mapInstanceDataId: this.mapDiv.dataset.toggleInstanceId || 'none'
        });

        // If container not found in this map div, create a new one
        if (!container) {
            log.debug(`MapRouteToggleButtonsGoogle: Creating new container for map ${this.mapDiv.id || 'unknown'}`);
            container = document.createElement('div') as HTMLElement;
            container.id = this.containerId;

            // Store identifying information on the container
            container.dataset.mapId = this.mapDiv.id || '';
            container.dataset.toggleInstanceId = this.mapDiv.dataset.toggleInstanceId || '';

            // Style the container
            container.style.position = 'absolute';
            container.style.bottom = '16px';
            container.style.left = '50%';
            container.style.transform = 'translateX(-50%)';
            container.style.display = 'flex';
            container.style.gap = '16px';
            container.style.zIndex = '9999'; // Higher z-index to ensure visibility in fullscreen
            container.style.background = 'rgba(255,255,255,0.9)';
            container.style.borderRadius = '8px';
            container.style.padding = '8px 16px';
            container.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';

            // Add the container ONLY to this map div
            this.appendToParent(container);
        } else {
            log.debug('MapRouteToggleButtonsGoogle: Reusing existing container');
        }
        container.innerHTML = '';
        this.container = container;

        // Helper to create a toggle button
        const createToggleButton = (color: string, visible: boolean, baseLabel: string, onClick: () => void) => {
            const btn = document.createElement('button');
            btn.style.display = 'flex';
            btn.style.alignItems = 'center';
            btn.style.margin = '0 8px';
            btn.style.padding = '4px 12px';
            btn.style.border = '1px solid #ccc';
            btn.style.borderRadius = '4px';
            btn.style.background = visible ? '#f0f0f0' : '#fff';
            btn.style.cursor = 'pointer';
            btn.style.fontSize = '14px';
            btn.style.boxShadow = visible ? '0 1px 4px rgba(0,0,0,0.08)' : 'none';
            // Color line
            const colorLine = document.createElement('span');
            colorLine.style.width = '32px';
            colorLine.style.height = '4px';
            colorLine.style.background = color;
            colorLine.style.display = 'inline-block';
            colorLine.style.marginRight = '8px';
            colorLine.style.borderRadius = '2px';
            btn.appendChild(colorLine);
            const text = document.createElement('span');
            text.textContent = `${visible ? 'Hide' : 'Show'} ${baseLabel}`;
            btn.appendChild(text);
            btn.onclick = onClick;
            return btn;
        };

        // Create and append buttons to container
        buttonConfigs.forEach(cfg => {
            container.appendChild(createToggleButton(cfg.color, cfg.visible, cfg.baseLabel, cfg.onClick));
        });
    }

    private appendToParent(container: HTMLElement) {
        // Add instance identifiers to the container for debugging and tracking
        container.dataset.toggleInstanceId = this.uniqueInstanceId;
        container.dataset.originalMapId = this.originalMapId;

        log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Appending container to parent`, {
            containerId: container.id,
            hasParent: !!container.parentNode,
            mapDivExists: !!this.mapDiv,
            mapDivId: this.mapDiv?.id || 'no-id',
            originalMapId: this.originalMapId
        });

        // Safety check - if this mapDiv doesn't exist anymore, don't proceed
        if (!this.mapDiv || !document.body.contains(this.mapDiv)) {
            log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Map div is no longer in document, aborting`);
            return;
        }

        // Remove from any previous parent to avoid duplication
        if (container.parentNode) {
            log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Removing container from previous parent`);
            container.parentNode.removeChild(container);
        }

        // Try to find the Google Maps container in THIS specific map instance
        try {
            // First try to locate the Google Maps container within THIS map
            const gmStyleContainer = this.mapDiv.querySelector('.gm-style') || this.mapDiv.closest('.gm-style');
            log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Found Google Maps style container:`, !!gmStyleContainer);

            // The target is either the gm-style container or the map div itself
            const targetContainer = gmStyleContainer || this.mapDiv;

            // Safety check - we should never append to another map's container
            // We check both instance ID and original map ID to ensure we have the right map
            const isCorrectMapContext =
                targetContainer === gmStyleContainer ||
                targetContainer === this.mapDiv ||
                this.mapDiv.contains(targetContainer) ||
                (gmStyleContainer && gmStyleContainer.contains(this.mapDiv)) ||
                (targetContainer as HTMLElement).dataset?.originalMapId === this.originalMapId;

            if (!isCorrectMapContext) {
                log.error(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Trying to append to wrong map context, aborting`);
                return;
            }

            // Before appending, remove any previous container with the same ID from this specific map
            // First check within our target container
            const existingInTarget = targetContainer.querySelector(`#${this.containerId}`);
            if (existingInTarget && existingInTarget !== container) {
                log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Removing existing container from target`);
                existingInTarget.parentNode?.removeChild(existingInTarget);
            }

            // Ensure the container has high z-index to appear above map controls
            container.style.zIndex = '9999';

            // Mark the container with all tracking IDs
            container.dataset.mapId = this.mapDiv.id || 'unknown';
            container.dataset.originalMapId = this.originalMapId;
            container.dataset.toggleInstanceId = this.uniqueInstanceId;
            container.dataset.timestamp = new Date().toISOString();

            // Finally append to the target container
            targetContainer.appendChild(container);
            log.debug(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Container successfully appended to map`);
        } catch (error) {
            log.error(`MapRouteToggleButtonsGoogle [${this.uniqueInstanceId}]: Error appending container:`, error);
        }
    }

    // Helper method to render buttons directly in a container
    private renderButtonsInContainer(container: HTMLElement) {
        if (!container) return;

        // Get the instance ID from the container or use our instance ID
        const instanceId = container.dataset.toggleInstanceId || this.uniqueInstanceId;

        // Double-check this is our container before rendering
        if (instanceId !== this.uniqueInstanceId) {
            log.error(`MapRouteToggleButtonsGoogle: Container instance ID mismatch, expected ${this.uniqueInstanceId} but got ${instanceId}`);
            return;
        }

        log.debug(`MapRouteToggleButtonsGoogle [${instanceId}]: Rendering buttons in container`);

        // Verify this is our container
        if (container.id !== this.containerId) {
            log.error(`MapRouteToggleButtonsGoogle [${instanceId}]: Container ID mismatch, expected ${this.containerId} but got ${container.id}`);
            return;
        }

        container.innerHTML = '';

        // Helper to create a toggle button (same as in render method)
        const createToggleButton = (color: string, visible: boolean, baseLabel: string, onClick: () => void) => {
            const btn = document.createElement('button');
            btn.style.display = 'flex';
            btn.style.alignItems = 'center';
            btn.style.margin = '0 8px';
            btn.style.padding = '4px 12px';
            btn.style.border = '1px solid #ccc';
            btn.style.borderRadius = '4px';
            btn.style.background = visible ? '#f0f0f0' : '#fff';
            btn.style.cursor = 'pointer';
            btn.style.fontSize = '14px';
            btn.style.boxShadow = visible ? '0 1px 4px rgba(0,0,0,0.08)' : 'none';
            // Color line
            const colorLine = document.createElement('span');
            colorLine.style.width = '32px';
            colorLine.style.height = '4px';
            colorLine.style.background = color;
            colorLine.style.display = 'inline-block';
            colorLine.style.marginRight = '8px';
            colorLine.style.borderRadius = '2px';
            btn.appendChild(colorLine);
            // Label
            const text = document.createElement('span');
            text.textContent = `${visible ? 'Hide' : 'Show'} ${baseLabel}`;
            btn.appendChild(text);
            btn.onclick = onClick;
            // Store the map/toggle ID on the button to help with debugging
            btn.dataset.toggleInstanceId = this.uniqueInstanceId;
            btn.dataset.originalMapId = this.originalMapId;
            return btn;
        };

        // Store current routeData for any callbacks
        // Instead of using global window storage, store instance-specific data in the container
        const currentRouteData = container.dataset.routeData
            ? JSON.parse(container.dataset.routeData)
            : (window as any).__routeDataForToggle || [];

        // Try to get stored options from the container first, fall back to global
        const storedOptions = container.dataset.routeOptions
            ? JSON.parse(container.dataset.routeOptions)
            : (window as any).__lastRouteToggleOptions || {};

        // Check if we have any valid routes to display buttons for
        // Apply same visibility rules as in the render method
        const showGoogle = storedOptions.showGoogle && this.routeVisibility.google !== undefined;
        const showMileMaker = storedOptions.showMileMaker && this.routeVisibility.milemaker !== undefined;
        const showTrimble = storedOptions.showTrimble && this.routeVisibility.trimble !== undefined;

        // Log visibility state for debugging
        log.debug(`MapRouteToggleButtonsGoogle [${instanceId}]: Rendering buttons with state:`, {
            showGoogle,
            showMileMaker,
            showTrimble,
            googleVisibility: this.routeVisibility.google,
            mileMakerVisibility: this.routeVisibility.milemaker,
            trimbleVisibility: this.routeVisibility.trimble,
            mapId: this.mapDiv?.id || 'unknown',
            originalMapId: this.originalMapId,
            containerId: container.id
        });

        // Define button configurations based on current state and visibility flags
        const buttonConfigs = [
            showGoogle && {
                color: '#0000ff',
                visible: this.routeVisibility.google,
                baseLabel: 'Google non-truck route',
                onClick: () => {
                    this.routeVisibility.google = !this.routeVisibility.google;
                    this.callbacks.google(this.routeVisibility.google);
                    // Re-render in the same container
                    this.renderButtonsInContainer(container);
                }
            },
            showMileMaker && {
                color: '#ff0000',
                visible: this.routeVisibility.milemaker,
                baseLabel: 'MileMaker Truck Route',
                onClick: () => {
                    // Toggle the visibility state
                    const newVisibility = !this.routeVisibility.milemaker;
                    this.routeVisibility.milemaker = newVisibility;
                    log.debug(`[${instanceId}] Toggled MileMaker visibility to:`, newVisibility);

                    // Call the callback to update the map
                    this.callbacks.milemaker(newVisibility);

                    // Call handleRouteToggle to fetch data if needed
                    if (newVisibility && this.callbacks.handleRouteToggle) {
                        this.callbacks.handleRouteToggle(currentRouteData);
                    }

                    // Re-render in the same container
                    this.renderButtonsInContainer(container);
                }
            },
            showTrimble && {
                color: '#FF0000',
                visible: this.routeVisibility.trimble,
                baseLabel: 'PC*Miler Truck Route',
                onClick: () => {
                    // Toggle the visibility state
                    const newVisibility = !this.routeVisibility.trimble;
                    this.routeVisibility.trimble = newVisibility;
                    log.debug(`[${instanceId}] Toggled PC*Miler visibility to:`, newVisibility);

                    // Call the callback to update the map
                    this.callbacks.trimble(newVisibility);

                    // Call handleTrimbleRouteToggle to fetch data if needed
                    if (newVisibility && this.callbacks.handleTrimbleRouteToggle) {
                        this.callbacks.handleTrimbleRouteToggle(currentRouteData);
                    }

                    // Re-render in the same container
                    this.renderButtonsInContainer(container);
                }
            }
        ].filter(Boolean);

        // If no buttons should be shown, hide the container
        if (buttonConfigs.length === 0) {
            log.debug(`MapRouteToggleButtonsGoogle [${instanceId}]: No toggle buttons to show, hiding container`);
            container.style.display = 'none';
            return;
        }

        // Make sure container is visible
        container.style.display = 'flex';

        // Add buttons to container
        buttonConfigs.forEach(cfg => {
            container.appendChild(createToggleButton(cfg.color, cfg.visible, cfg.baseLabel, cfg.onClick));
        });
    }

    // Clean up this instance and remove event listeners
    public destroy() {
        // Remove from global registry using the uniqueInstanceId
        if ((window as any).__mapToggleInstances && (window as any).__mapToggleInstances[this.uniqueInstanceId]) {
            delete (window as any).__mapToggleInstances[this.uniqueInstanceId];
            log.debug(`MapRouteToggleButtonsGoogle: Removed instance ${this.uniqueInstanceId} from global registry`);
        }

        // Remove any containers associated with this instance
        const containers = document.querySelectorAll(`#${this.containerId}`);
        containers.forEach(container => {
            if (container.parentNode) {
                container.parentNode.removeChild(container);
                log.debug(`MapRouteToggleButtonsGoogle: Removed container ${this.containerId} from DOM`);
            }
        });

        // Clean up event listeners if any were added directly by this instance
        document.removeEventListener('fullscreenchange', this._handleFullscreenChange);

        // Remove data attributes from the map div if it still exists
        if (this.mapDiv && document.body.contains(this.mapDiv)) {
            // Only remove our own instance ID, not others
            if (this.mapDiv.dataset.toggleInstanceId === this.uniqueInstanceId) {
                delete this.mapDiv.dataset.toggleInstanceId;
            }
        }

        log.debug(`MapRouteToggleButtonsGoogle: Destroyed instance ${this.uniqueInstanceId} for map ${this.mapInstanceId}`);
    }

    // The bound fullscreen change handler reference
    private _handleFullscreenChange = () => this._onFullscreenChange();

    // Handle fullscreen changes
    private _onFullscreenChange() {
        // Give the DOM time to update after fullscreen change
        setTimeout(() => {
            log.debug(`MapRouteToggleButtonsGoogle: Fullscreen change detected for map ${this.mapInstanceId}`);
        }, 500);
    }

    public reappendOnFullscreen() {
        // Add the fullscreen change event listener
        document.addEventListener('fullscreenchange', this._handleFullscreenChange);

        const handleFullscreenChange = () => {
            // Give the DOM time to update after fullscreen change
            setTimeout(() => {
                log.debug('Fullscreen change detected, checking if buttons should be displayed');

                // First determine if we need to show any toggle buttons at all
                // Get the stored route options
                const routeOptions = (window as any).__lastRouteToggleOptions || {};
                log.debug('Fullscreen change - using stored route options:', routeOptions);

                // Define button configurations - This is the same logic from the render method
                const buttonConfigs = [
                    // Only show Google toggle if route exists and is enabled in options
                    routeOptions.showGoogle && this.routeVisibility.google !== undefined && {
                        color: '#0000ff',
                        visible: this.routeVisibility.google,
                        baseLabel: 'Google non-truck route',
                        onClick: () => {}
                    },
                    // Only show MileMaker toggle if it's enabled in options
                    routeOptions.showMileMaker && this.routeVisibility.milemaker !== undefined && {
                        color: '#ff0000',
                        visible: this.routeVisibility.milemaker,
                        baseLabel: 'MileMaker Truck Route',
                        onClick: () => {}
                    }
                ].filter(Boolean);

                // Get unique identifier for this toggle instance
                const uniqueInstanceId = this.uniqueInstanceId;

                // If no buttons should be shown, remove any existing container and exit
                if (buttonConfigs.length === 0) {
                    log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: No toggle buttons to show on fullscreen change`);

                    // Only search for containers with our specific ID
                    const containers = document.querySelectorAll(`#${this.containerId}`);
                    containers.forEach(container => {
                        const htmlContainer = container as HTMLElement;
                        if (htmlContainer.dataset.toggleInstanceId === uniqueInstanceId && container.parentNode) {
                            log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Removing container from parent`);
                            container.parentNode.removeChild(container);
                        }
                    });

                    this.container = null;
                    return;
                }

                // Check if we're in fullscreen mode
                const isFullscreen = !!document.fullscreenElement;
                log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Fullscreen state:`, isFullscreen);

                // IMPORTANT: Find only the map element that matches THIS instance
                let targetMapDiv = this.mapDiv;

                // Look for elements with our original map ID, which should be preserved across fullscreen transitions
                if (isFullscreen) {
                    // When entering fullscreen, we need to find the correct fullscreen container
                    const fullscreenElement = document.fullscreenElement;

                    if (fullscreenElement) {
                        const fsElement = fullscreenElement as HTMLElement;

                        // First try to match by originalMapId (preferred)
                        if (fsElement.dataset.originalMapId === this.originalMapId) {
                            log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Found fullscreen element with matching original map ID`);
                            targetMapDiv = fsElement;
                        }
                        // Then try to match by toggleInstanceId
                        else if (fsElement.dataset.toggleInstanceId === uniqueInstanceId) {
                            log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Found fullscreen element with matching toggle instance ID`);
                            targetMapDiv = fsElement;
                        }
                        // If all else fails, use the fullscreen element
                        else {
                            // Look for Google Maps container inside the fullscreen element
                            const mapInFullscreen = fullscreenElement.querySelector('.gm-style');
                            if (mapInFullscreen) {
                                log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Found map in fullscreen element`);
                                targetMapDiv = mapInFullscreen as HTMLElement;
                            } else {
                                log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Using fullscreen element as target`);
                                targetMapDiv = fullscreenElement as HTMLElement;
                            }
                        }

                        // Mark the fullscreen element with our tracking IDs
                        targetMapDiv.dataset.toggleInstanceId = uniqueInstanceId;
                        targetMapDiv.dataset.originalMapId = this.originalMapId;
                    }
                }

                // Make sure we have a valid target map div
                if (!targetMapDiv || !document.body.contains(targetMapDiv)) {
                    log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Invalid target map div, aborting`);
                    return;
                }

                log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Using target map:`,
                    targetMapDiv.id || 'no-id', 'in fullscreen:', isFullscreen);

                // Create a new container specific to THIS instance
                const newContainer = document.createElement('div');

                // Use our unique instance-specific container ID
                newContainer.id = this.containerId;

                // Store instance identification data on the container
                newContainer.dataset.toggleInstanceId = uniqueInstanceId;
                newContainer.dataset.originalMapId = this.originalMapId;
                newContainer.dataset.timestamp = new Date().toISOString();

                // Store route information on the container
                if (routeOptions) {
                    try {
                        newContainer.dataset.routeOptions = JSON.stringify(routeOptions);
                    } catch (e) {
                        log.error(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Error storing route options:`, e);
                    }
                }

                // Style the container
                newContainer.style.position = 'absolute';
                newContainer.style.bottom = '16px';
                newContainer.style.left = '50%';
                newContainer.style.transform = 'translateX(-50%)';
                newContainer.style.display = 'flex';
                newContainer.style.gap = '16px';
                newContainer.style.zIndex = '9999';
                newContainer.style.background = 'rgba(255,255,255,0.9)';
                newContainer.style.borderRadius = '8px';
                newContainer.style.padding = '8px 16px';
                newContainer.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';

                // Remove any existing containers with our ID from the target
                const existingContainers = targetMapDiv.querySelectorAll(`#${this.containerId}`);
                existingContainers.forEach(container => {
                    if (container.parentNode) {
                        log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Removing existing container from target`);
                        container.parentNode.removeChild(container);
                    }
                });

                try {
                    // Update the map div reference before appending
                    this.mapDiv = targetMapDiv;

                    // Make sure we don't lose our original map ID when updating the map div
                    // This is critical for maintaining identity across fullscreen transitions
                    if (!this.originalMapId) {
                        this.originalMapId = targetMapDiv.dataset.originalMapId || targetMapDiv.id || '';
                    }

                    // Append the container to the target map div using our custom method
                    this.appendToParent(newContainer);
                    this.container = newContainer;

                    // Re-render the buttons
                    this.renderButtonsInContainer(newContainer);

                    log.debug(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Toggle buttons reappended to`,
                              isFullscreen ? 'fullscreen map' : 'original map',
                              'with MileMaker visibility:', this.routeVisibility.milemaker);
                } catch (error) {
                    log.error(`MapRouteToggleButtonsGoogle [${uniqueInstanceId}]: Error during fullscreen change:`, error);
                }
            }, 500); // Increased delay to ensure DOM is fully updated
        };

        // Listen for fullscreen changes, using an instance method to make it easier to remove later
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        // Also handle the case when the map is already in fullscreen mode
        if (document.fullscreenElement) {
            handleFullscreenChange();
        }

        // Return the handler so it can be removed if needed
        return handleFullscreenChange;
    }

    /**
     * Method to merge route toggle options with existing options
     * This ensures we don't lose toggle buttons when re-rendering
     */
    public updateOptions(options: RouteToggleOptions): void {
        // Create new container only if it doesn't exist
        if (!this.container) {
            this.render(options);
            return;
        }

        log.debug('MapRouteToggleButtonsGoogle: Updating options:', {
            showGoogle: options.showGoogle,
            showMileMaker: options.showMileMaker,
            showTrimble: options.showTrimble
        });

        // Store the options for future reference
        (window as any).__lastRouteToggleOptions = options;

        // Simply re-render with the new options
        // This will preserve existing buttons and add new ones as needed
        this.render(options);
    }
}
